import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Circle, Star, Hexagon, Triangle, Square, Diamond, Heart, DoorOpen, Eye, EyeOff, Hash } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CORE_NODES = new Set(['A', 'B', 'V', 'G', 'D', 'E', 'Zh', 'Z', 'I', 'K', 'L', 'M', 'HRT', 'Man', 'SUN']);
const CORE_ORDER = ['A', 'B', 'V', 'G', 'D', 'E', 'Zh', 'Z', 'I', 'K', 'L', 'M', 'HRT', 'Man', 'SUN'];

const SHAPE_OPTIONS = [
  { value: 'circle', label: 'Dairə', Icon: Circle },
  { value: 'star', label: 'Ulduz', Icon: Star },
  { value: 'hexagon', label: 'Altıbucaq', Icon: Hexagon },
  { value: 'triangle', label: 'Üçbucaq', Icon: Triangle },
  { value: 'square', label: 'Kvadrat', Icon: Square },
  { value: 'diamond', label: 'Romb', Icon: Diamond },
  { value: 'heart', label: 'Ürək', Icon: Heart },
  { value: 'door', label: 'Qapı', Icon: DoorOpen },
  { value: 'arch', label: 'Arka qapı (cənnət)', Icon: DoorOpen },
  { value: 'portal', label: 'İşıq portalı', Icon: Circle },
  { value: 'columns', label: 'Sütunlar + arka', Icon: DoorOpen },
];

const DEFAULT_COLORS = {
  A: '#f97316', B: '#a855f7', V: '#f97316', G: '#22c55e',
  D: '#4f46e5', E: '#ef4444', Zh: '#3b82f6', Z: '#4f46e5',
  I: '#0ea5e9', K: '#6366f1', L: '#0ea5e9', M: '#eab308',
  HRT: '#10b981', Man: '#f59e0b', SUN: '#fbbf24',
};

const DEFAULT_LABELS = {
  A: { letter: 'А', desc: 'Gün' }, B: { letter: 'Б', desc: 'Ay' },
  V: { letter: 'В', desc: 'İl' }, G: { letter: 'Г', desc: 'Yekun' },
  D: { letter: 'Д', desc: 'NƏ1' }, E: { letter: 'Е', desc: 'NƏ2' },
  Zh: { letter: 'Ж', desc: 'NƏ3' }, Z: { letter: 'З', desc: 'NƏ4' },
  I: { letter: 'И', desc: 'KD1' }, K: { letter: 'К', desc: 'KD2' },
  L: { letter: 'Л', desc: 'KD3' }, M: { letter: 'М', desc: 'KD4' },
  HRT: { letter: 'HRT', desc: 'Heart' }, Man: { letter: 'Man', desc: 'Manipura' },
  SUN: { letter: '☀', desc: 'Sun' },
};

const LabelEditor = ({
  nodeLabels,
  nodeColors,
  nodeTextColors,
  nodeShapes,
  hiddenSpheres,
  hiddenValues,
  token,
  onLabelsChange,
  onColorsChange,
  onTextColorsChange,
  onShapesChange,
  onHiddenSpheresChange,
  onHiddenValuesChange,
  onNodeAdded,
  onNodeDeleted,
}) => {
  const labels = { ...DEFAULT_LABELS, ...nodeLabels };
  const colors = { ...DEFAULT_COLORS, ...nodeColors };
  const textColors = nodeTextColors || {};
  const shapes = nodeShapes || {};
  const hidden = new Set(hiddenSpheres || []);
  const hiddenVals = new Set(hiddenValues || []);

  const toggleHidden = (id) => {
    if (!onHiddenSpheresChange) return;
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onHiddenSpheresChange(Array.from(next));
  };

  const toggleHiddenValue = (id) => {
    if (!onHiddenValuesChange) return;
    const next = new Set(hiddenVals);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onHiddenValuesChange(Array.from(next));
  };

  const handleTextColorChange = (id, value) => {
    if (!onTextColorsChange) return;
    onTextColorsChange({ ...textColors, [id]: value });
  };

  // Ordered list: core first, then custom in insertion order
  const allIds = [
    ...CORE_ORDER.filter(id => id in labels),
    ...Object.keys(labels).filter(id => !CORE_NODES.has(id)),
  ];

  const [showAdd, setShowAdd] = useState(false);
  const [newId, setNewId] = useState('');
  const [newLetter, setNewLetter] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newColor, setNewColor] = useState('#a855f7');
  const [newShape, setNewShape] = useState('circle');
  const [msg, setMsg] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleLabelChange = (id, field, value) => {
    const updated = { ...labels, [id]: { ...labels[id], [field]: value } };
    onLabelsChange(updated);
  };

  const handleColorChange = (id, value) => {
    onColorsChange({ ...colors, [id]: value });
  };

  const handleShapeChange = (id, value) => {
    const updated = { ...shapes, [id]: value };
    onShapesChange(updated);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await axios.post(`${API}/admin/nodes`, {
        id: newId, letter: newLetter, desc: newDesc,
        color: newColor, shape: newShape,
        x: 300, y: 500,
      }, authHeaders);
      setMsg('Node yaradıldı!');
      onNodeAdded && onNodeAdded(res.data.id);
      setNewId(''); setNewLetter(''); setNewDesc('');
      setNewColor('#a855f7'); setNewShape('circle');
      setShowAdd(false);
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Xəta: ' + (err.response?.data?.detail || 'Yaratma uğursuz'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`"${id}" node-u silmək istəyirsiniz? Bütün əlaqələri də silinəcək.`)) return;
    try {
      await axios.delete(`${API}/admin/nodes/${encodeURIComponent(id)}`, authHeaders);
      setMsg(`"${id}" silindi`);
      onNodeDeleted && onNodeDeleted(id);
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Xəta: ' + (err.response?.data?.detail || 'Silinmə uğursuz'));
    }
  };

  return (
    <div className="admin-section" data-testid="label-editor-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="admin-section-title mb-0">Sfera hərfləri, rəngləri və fiqurları</h3>
        <button
          className="admin-btn-primary"
          onClick={() => setShowAdd(!showAdd)}
          data-testid="toggle-add-node-btn"
        >
          <Plus size={16} /> {showAdd ? 'Ləğv et' : 'Yeni node'}
        </button>
      </div>
      <p className="admin-section-desc">
        Hər sferanın görünüşünü redaktə edin. Yeni custom node yarada bilərsiniz — core node-lar silinə bilməz.
      </p>

      {msg && (
        <p className={`text-sm mb-3 ${msg.startsWith('Xəta') ? 'text-red-400' : 'text-green-400'}`} data-testid="label-editor-msg">
          {msg}
        </p>
      )}

      {/* Add new node form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="new-node-form" data-testid="new-node-form">
          <h4 className="user-form-title">Yeni node əlavə et</h4>
          <div className="new-node-row">
            <input
              type="text"
              className="karma-input text-left"
              placeholder="ID (məs: Root)"
              value={newId}
              onChange={e => setNewId(e.target.value)}
              minLength={1}
              maxLength={20}
              required
              data-testid="new-node-id"
            />
            <input
              type="text"
              className="karma-input text-left"
              placeholder="Hərf (məs: R)"
              value={newLetter}
              onChange={e => setNewLetter(e.target.value)}
              maxLength={8}
              required
              data-testid="new-node-letter"
            />
            <input
              type="text"
              className="karma-input text-left"
              placeholder="Açıqlama"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              maxLength={30}
              data-testid="new-node-desc"
            />
            <input
              type="color"
              className="label-color-picker"
              value={newColor}
              onChange={e => setNewColor(e.target.value)}
              title="Rəng"
              data-testid="new-node-color"
            />
            <select
              className="karma-input text-left"
              value={newShape}
              onChange={e => setNewShape(e.target.value)}
              data-testid="new-node-shape"
            >
              {SHAPE_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <button type="submit" className="admin-btn-primary" data-testid="submit-new-node">
              Yarat
            </button>
          </div>
        </form>
      )}

      <div className="label-editor-grid">
        {allIds.map(id => {
          const label = labels[id] || { letter: id, desc: '' };
          const color = colors[id] || '#a855f7';
          const textColor = textColors[id] || '#ffffff';
          const shape = shapes[id] || 'circle';
          const isCustom = !CORE_NODES.has(id);

          return (
            <div key={id} className={`label-editor-item ${isCustom ? 'label-editor-item-custom' : ''}`} data-testid={`label-item-${id}`}>
              <input
                type="color"
                className="label-color-picker"
                value={color}
                onChange={e => handleColorChange(id, e.target.value)}
                title="Fiqurun rəngini dəyiş"
              />
              <input
                type="color"
                className="label-color-picker label-text-color-picker"
                value={textColor}
                onChange={e => handleTextColorChange(id, e.target.value)}
                title="Hərf/rəqəm rəngini dəyiş"
                data-testid={`text-color-${id}`}
              />
              <div className="label-editor-id">
                {id}
                {isCustom && <span className="label-custom-badge">custom</span>}
              </div>
              <div className="label-editor-fields">
                <div className="label-field-group">
                  <span className="label-field-label">Hərf</span>
                  <input
                    type="text"
                    className="label-field-input"
                    value={label.letter}
                    onChange={e => handleLabelChange(id, 'letter', e.target.value)}
                    maxLength={8}
                  />
                </div>
                <div className="label-field-group">
                  <span className="label-field-label">Açıqlama</span>
                  <input
                    type="text"
                    className="label-field-input label-field-desc"
                    value={label.desc || ''}
                    onChange={e => handleLabelChange(id, 'desc', e.target.value)}
                    maxLength={30}
                  />
                </div>
                <div className="label-field-group">
                  <span className="label-field-label">Fiqur</span>
                  <select
                    className="label-field-input"
                    value={shape}
                    onChange={e => handleShapeChange(id, e.target.value)}
                    data-testid={`shape-select-${id}`}
                  >
                    {SHAPE_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                className={`label-visibility-btn ${hidden.has(id) ? 'label-visibility-btn-off' : ''}`}
                onClick={() => toggleHidden(id)}
                title={hidden.has(id) ? 'Fiquru göstər' : 'Fiquru gizlət (yalnız hərf + dəyər qalsın)'}
                data-testid={`toggle-visibility-${id}`}
                type="button"
              >
                {hidden.has(id) ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                className={`label-value-btn ${hiddenVals.has(id) ? 'label-value-btn-off' : ''}`}
                onClick={() => toggleHiddenValue(id)}
                title={hiddenVals.has(id) ? 'Dəyəri göstər' : 'Dəyəri gizlət (yalnız hərf qalsın)'}
                data-testid={`toggle-value-${id}`}
                type="button"
              >
                <Hash size={14} />
              </button>
              {isCustom && (
                <button
                  className="label-delete-btn"
                  onClick={() => handleDelete(id)}
                  title="Sil"
                  data-testid={`delete-node-${id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LabelEditor;
